import { useState, useEffect } from 'react';
import { Coins, TrendingUp, CreditCard } from 'lucide-react';
import { API_URL } from '../../config/api';

const ExpenseAnalysis = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        let params = `start_date=${dr.start}&end_date=${dr.end}`;
        if (reportStoreFilter === 'gold') params += '&store_id=1';
        else if (reportStoreFilter !== 'all' && reportStoreFilter !== 'silver') params += `&store_id=${reportStoreFilter}`;

        const res = await fetch(`${API_URL}/reports/expense-stats?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Gider verileri yükleniyor...</p></div>;
  if (!data) return <div className="empty-state"><div className="empty-icon">💰</div><p>Veri bulunamadı</p></div>;

  const totalExpense = (data.byCategory || []).reduce((s, c) => s + parseFloat(c.total_amount || 0), 0);
  const totalCount = (data.byCategory || []).reduce((s, c) => s + (c.count || 0), 0);
  const categoryCount = (data.byCategory || []).length;
  const maxCategoryAmount = Math.max(...(data.byCategory || []).map(c => parseFloat(c.total_amount) || 0), 1);

  // Gelir vs Gider karşılaştırma
  const months = [...new Set([
    ...(data.monthly || []).map(m => m.month),
    ...(data.monthlyIncome || []).map(m => m.month),
  ])].sort();

  const incomeMap = {};
  (data.monthlyIncome || []).forEach(m => { incomeMap[m.month] = parseFloat(m.total_income) || 0; });
  const expenseMap = {};
  (data.monthly || []).forEach(m => { expenseMap[m.month] = parseFloat(m.total_expense) || 0; });

  const maxMonthly = Math.max(...months.map(m => Math.max(incomeMap[m] || 0, expenseMap[m] || 0)), 1);

  return (
    <div className="tab-content">
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Coins size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalExpense)}</div>
            <div className="stat-label">Toplam Gider</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><CreditCard size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Gider Kaydı</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><TrendingUp size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{categoryCount}</div>
            <div className="stat-label">Gider Kategorisi</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalCount > 0 ? totalExpense / totalCount : 0)}</div>
            <div className="stat-label">Ort. Gider</div>
          </div>
        </div>
      </div>

      {/* Kategori Dağılımı */}
      {data.byCategory && data.byCategory.length > 0 && (
        <div className="chart-card">
          <h3>Kategori Bazlı Giderler</h3>
          <div className="horizontal-bars">
            {data.byCategory.map((c, i) => (
              <div key={c.category} className="h-bar-item">
                <div className="h-bar-label">
                  <span className="h-bar-name">{c.category}</span>
                  <span className="h-bar-value">{c.count} adet • {formatCurrency(c.total_amount)} ({(parseFloat(c.total_amount) / totalExpense * 100).toFixed(1)}%)</span>
                </div>
                <div className="h-bar-track">
                  <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(parseFloat(c.total_amount) / maxCategoryAmount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ödeme Yöntemi Dağılımı */}
      {data.byMethod && data.byMethod.length > 0 && (
        <div className="chart-card">
          <h3>Ödeme Yöntemi Dağılımı</h3>
          <div className="horizontal-bars">
            {data.byMethod.map((m, i) => (
              <div key={m.payment_method} className="h-bar-item">
                <div className="h-bar-label">
                  <span className="h-bar-name">{m.payment_method}</span>
                  <span className="h-bar-value">{formatCurrency(m.total_amount)}</span>
                </div>
                <div className="h-bar-track">
                  <div className={`h-bar-fill color-${(i % 5) + 1}`} style={{ width: `${(parseFloat(m.total_amount) / totalExpense * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gelir vs Gider Aylık Karşılaştırma */}
      {months.length > 0 && (
        <div className="chart-card">
          <h3>Aylık Gelir vs Gider</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12 }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#0f766e', borderRadius: 2, marginRight: 4 }} />Gelir</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#e74c3c', borderRadius: 2, marginRight: 4 }} />Gider</span>
          </div>
          {months.map(month => {
            const income = incomeMap[month] || 0;
            const expense = expenseMap[month] || 0;
            return (
              <div key={month} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, fontWeight: 600 }}>
                  <span>{month}</span>
                  <span style={{ color: income - expense > 0 ? '#0f766e' : '#e74c3c' }}>
                    Net: {formatCurrency(income - expense)}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 20, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#0f766e', borderRadius: 3, width: `${(income / maxMonthly) * 100}%`, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 10, color: '#5a6c7d', marginTop: 2 }}>{formatCurrency(income)}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 20, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#e74c3c', borderRadius: 3, width: `${(expense / maxMonthly) * 100}%`, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: 10, color: '#5a6c7d', marginTop: 2 }}>{formatCurrency(expense)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExpenseAnalysis;
